import { catchError, exhaustMap, filter, forkJoin, map, of, scan, tap } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppStore } from '@navstation-portal/reporting/data-access/app';
import { vesselReportsEvents } from '@navstation-portal/reporting/data-access/vessel-reports';
import { GroupedFormData, ReportFormRuleTemplate } from '@navstation-portal/reporting/data-model/ui';
import {
  BunkerState,
  CargoDetailsState,
  ConsumptionsState,
  FormState,
  INIT_MODES,
  IReport,
  OperationalState,
  PositionAndWeatherState,
  PowerState,
  ReportStatuses,
  ReportType,
  StateField,
  StateFieldType,
  StockState,
  generateLocalReportId,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { toastEvents } from '@navstation-portal/shared/data-access/toaster';
import { VesselSpecService } from '@navstation-portal/vessels/data-access/service';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { signalStoreFeature } from '@ngrx/signals';
import { Dispatcher, Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { bunkerEvents } from './bunker/bunker-events';
import { BunkerStore } from './bunker/bunker.store';
import { cargoDetailsFormEvents } from './cargo-details/cargo-details-events';
import { CargoDetailsStore } from './cargo-details/cargo-details.store';
import { consumptionsFormEvents } from './consumptions/consumptions-events';
import { ConsumptionsStore } from './consumptions/consumptions.store';
import { operationalEvents } from './operational/operational-events';
import { OperationalStore } from './operational/operational.store';
import { positionAndWeatherEvents } from './position-and-weather/position-and-weather-events';
import { PositionAndWeatherStore } from './position-and-weather/position-and-weather.store';
import { powerEvents } from './power/power-events';
import { PowerStore, isShorePowerCarryOver } from './power/power.store';
import { REPORT_API_ADAPTER, REPORT_API_SERVICE } from './report-dependencies';
import { reportEvents } from './report-events';
import { buildReducedVesselReport } from './report-reduced.builder';
import { stockEvents } from './stock/stock-events';
import { StockStore } from './stock/stock.store';
import { isStateField } from './vm-builder-helpers';

interface ReportStoreState {
  reportStatus: () => string | null;
}

export function withReportEventHandlers() {
  return signalStoreFeature(
    withEventHandlers((rawStore) => {
      const store = rawStore as unknown as ReportStoreState;
      const events = inject(Events);
      const dispatcher = inject(Dispatcher);
      const toastDispatcher = injectDispatch(toastEvents);
      const reportService = inject(REPORT_API_SERVICE);
      const vesselSpecService = inject(VesselSpecService);
      const reportAdapterService = inject(REPORT_API_ADAPTER);
      const router = inject(Router);
      const appStore = inject(AppStore);

      const operationalStore = inject(OperationalStore);
      const positionAndWeatherStore = inject(PositionAndWeatherStore);
      const powerStore = inject(PowerStore);
      const cargoDetailsStore = inject(CargoDetailsStore);
      const consumptionsStore = inject(ConsumptionsStore);
      const bunkerStore = inject(BunkerStore);
      const stockStore = inject(StockStore);

      const unwrapValue = (value: unknown): unknown => {
        if (
          value &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          isStateField(value as StateField<StateFieldType>)
        ) {
          return (value as StateField<StateFieldType>).value;
        }
        if (Array.isArray(value)) {
          return value.map(unwrapValue);
        }
        if (value && typeof value === 'object') {
          return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, val]) => [key, unwrapValue(val)])
          );
        }
        return value;
      };

      const unwrapFormState = (formState: FormState): Record<string, unknown> =>
        unwrapValue(formState) as Record<string, unknown>;

      const normalizePower = (raw: Record<string, unknown>): PowerState => {
        const machinery = (raw?.['machineryRunningHours'] ?? {}) as Record<string, unknown[]>;
        const generators = (raw?.['generatorEngines'] ?? {}) as Record<string, unknown>;
        const machineryRh = [
          ...(machinery['mainEngines'] ?? []),
          ...(machinery['auxEngines'] ?? []),
          ...(machinery['mainAirCompressors'] ?? []),
          ...(machinery['auxBoilers'] ?? []),
          ...(machinery['compositeBoilers'] ?? []),
          ...(machinery['cargoCompressors'] ?? []),
          ...(machinery['bogCompressors'] ?? []),
          ...(machinery['cargoPumps'] ?? []),
          ...(machinery['ballastPumps'] ?? []),
          ...(machinery['shaftGeneratorPTO'] ?? []),
          ...(machinery['thrusters'] ?? []),
          ...(machinery['scr'] ?? []),
        ];

        // The turbo generator lives alongside the aux-engine array as flat
        // fields on the generatorEngines section; fold it back into the array
        // as a keyed entry (see convertPowerToApi) when any value is present.
        const auxEngines = (generators['auxEngines'] ?? []) as unknown[];
        const turboFields = {
          power: generators['turboPower'],
          maxRating: generators['turboMaxRating'],
          runningHours: generators['turboRunningHours'],
          chargeAirPressure: generators['turboChargeAirPressure'],
        };
        const hasTurbo = Object.values(turboFields).some((value) => value != null);
        const generatorEngines = hasTurbo ? [...auxEngines, { key: 'turboGenerator', ...turboFields }] : auxEngines;

        return {
          mainEngine: raw?.['mainEngine'] ?? {},
          machineryRh,
          flowMeterInstallationOld: raw?.['flowMeterInstallationOld'] ?? {},
          flowMeterInstallation: raw?.['flowMeterInstallation'] ?? {},
          pumps: raw?.['pumps'] ?? {},
          generatorEngines,
          scrubber: raw?.['scrubber'] ?? {},
          shaftGeneratorPTO: raw?.['shaftGeneratorPTO'] ?? {},
          environmental: raw?.['environmental'] ?? {},
          shorePower: raw?.['shorePower'] ?? {},
          cargoPumpsKwh: (raw?.['cargoPumpMeters'] as Record<string, unknown[]> | undefined)?.['pumps'] ?? [],
        } as unknown as PowerState;
      };

      const normalizeStock = (raw: Record<string, unknown>): StockState => {
        const tankRows = ((raw?.['tanks'] as Record<string, unknown>)?.['tankRows'] ?? []) as Record<string, unknown>[];
        const tanks: Record<string, unknown> = {};
        tankRows.forEach((row) => {
          const id = (row?.['id'] ?? row?.['_id']) as string | undefined;
          if (id) tanks[id] = row;
        });
        return {
          options: raw?.['options'],
          tankTransfers: (raw?.['tankTransfers'] as Record<string, unknown>)?.['transfers'] ?? [],
          fuelWaterRob: raw?.['fuelWaterRob'],
          lubeOilRob: raw?.['lubeOilRob'],
          oilLosses: raw?.['oilLosses'],
          tanks,
        } as unknown as StockState;
      };

      const normalizeOperational = (raw: Record<string, unknown>): OperationalState => {
        const voyageItinerary = (raw?.['voyageItinerary'] ?? {}) as Record<string, unknown>;
        return {
          ...raw,
          voyageItinerary: {
            ...voyageItinerary,
            inEca: operationalStore.vm().inEca,
            timeInEca: operationalStore.vm().timeInEca,
          },
        } as unknown as OperationalState;
      };

      const collateReport = (reportType: IReport['type']): IReport => ({
        type: reportType,
        operational: normalizeOperational(unwrapFormState(operationalStore.formState())),
        positionAndWeather: unwrapFormState(positionAndWeatherStore.formState()) as unknown as PositionAndWeatherState,
        power: normalizePower(unwrapFormState(powerStore.formState())),
        cargoDetails: unwrapFormState(cargoDetailsStore.formState()) as unknown as CargoDetailsState,
        consumption: unwrapFormState(consumptionsStore.formState()) as unknown as ConsumptionsState,
        stock: normalizeStock(unwrapFormState(stockStore.formState())),
        bunker: unwrapFormState(bunkerStore.formState()) as unknown as BunkerState,
      });

      // Reception port always falls back to the new report's operational port name;
      // shore power defaults to "in use" for new port/anchor reports unless the prior
      // report's session is still ongoing (already carried forward as in-use).
      const applyShorePowerCarryOverPatch = (reportType: ReportType, carryoverData: IReport) => {
        const priorShorePower = carryoverData.power?.shorePower;
        const receptionPort = carryoverData.operational?.portAndPosition?.portName ?? priorShorePower?.receptionPort;
        if (receptionPort != null) {
          dispatcher.dispatch(
            powerEvents.updateFormStateValue({
              sectionId: 'shorePower',
              fieldId: 'receptionPort',
              value: receptionPort,
            })
          );
        }

        const isPortOrAnchorReport = reportType === ReportType.Port || reportType === ReportType.Anchor;
        if (!isPortOrAnchorReport) return;

        if (isShorePowerCarryOver(priorShorePower)) return;

        dispatcher.dispatch(
          powerEvents.updateFormStateValue({ sectionId: 'shorePower', fieldId: 'isUsed', value: true })
        );
      };

      // A template that does not load gives an empty template, and the report
      // still opens. Each rule method fails open or fails closed when the
      // template holds no rule, so the form draws its markup fallback. An
      // error here must not stop the report, and a 404 here must not read as
      // 'report not found'.
      const getFormRuleTemplate = (vesselId: string) =>
        reportService.getReportFormRuleTemplate(vesselId).pipe(
          catchError((err) => {
            console.error('Error loading the form rule template', err);
            return of<ReportFormRuleTemplate>({ tabs: {} });
          })
        );

      const isReportNotFoundError = (error: unknown) => {
        if (typeof error !== 'object' || !error || !('status' in error)) {
          return false;
        }

        return (error as { status?: number }).status === 404;
      };

      const initialiseSharedForms = (
        payload: IReport,
        initMode: (typeof INIT_MODES)[keyof typeof INIT_MODES],
        formFieldRules: Record<string, GroupedFormData>,
        vesselSpec: VesselSpec | undefined
      ) => {
        dispatcher.dispatch(
          operationalEvents.initialiseFormState({
            initialData: payload.operational,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          cargoDetailsFormEvents.initialiseFormState({
            initialData: payload.cargoDetails,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          powerEvents.initialiseFormState({
            initialData: payload.power,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          positionAndWeatherEvents.initialiseFormState({
            initialData: payload.positionAndWeather,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          consumptionsFormEvents.initialiseFormState({
            initialData: payload.consumption,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          bunkerEvents.initialiseFormState({
            initialData: payload.bunker,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
        dispatcher.dispatch(
          stockEvents.initialiseFormState({
            initialData: payload.stock,
            initMode,
            formFieldRules,
            vesselSpec,
          })
        );
      };

      return {
        createNewReport$: events.on(reportEvents.createNewReport).pipe(
          exhaustMap(({ payload: { reportType } }) => {
            const vesselId = appStore.selectedVesselId();
            if (!vesselId) {
              return of(reportEvents.createReportFailed({ error: 'No vessel selected' }));
            }
            const localId = generateLocalReportId();
            dispatcher.dispatch(reportEvents.setLocalReportId(localId));
            dispatcher.dispatch(reportEvents.setReportType(reportType));

            return forkJoin({
              formFieldRules: reportService.getFormFieldRules(),
              formRuleTemplate: getFormRuleTemplate(vesselId),
              vesselSpec: vesselSpecService.getVesselSpec(vesselId).pipe(catchError(() => of(undefined))),
              carryoverData: reportService.getCarryoverReport(vesselId, undefined),
            }).pipe(
              map(({ formFieldRules, formRuleTemplate, vesselSpec, carryoverData }) => ({
                formFieldRules,
                formRuleTemplate,
                vesselSpec,
                carryoverData: reportAdapterService.convertFromAPI(carryoverData, vesselSpec),
              })),
              tap(({ formFieldRules, formRuleTemplate, carryoverData, vesselSpec }) => {
                dispatcher.dispatch(reportEvents.gotCarryoverData(carryoverData));
                dispatcher.dispatch(reportEvents.gotFormFieldRules(formFieldRules));
                dispatcher.dispatch(reportEvents.gotFormRuleTemplate(formRuleTemplate));
                initialiseSharedForms(carryoverData, INIT_MODES.CARRY_OVER, formFieldRules, vesselSpec);
                applyShorePowerCarryOverPatch(reportType, carryoverData);
                // Last, so the template wins over the hardcoded patch above.
                // Moving a field into the template is then a deletion from
                // that patch, and a deletion nobody made leaves the template
                // in charge rather than the stale code.
                //
                // It is dispatched here and nowhere else. `openReport$` holds
                // the same template and the same carry-over report, and it
                // must change no field of a report that already exists.
                dispatcher.dispatch(reportEvents.carryOverOnCreate());
                router.navigate(['site', vesselId, 'vessel-reports', localId, 'operational']);
              }),
              map(() => reportEvents.createReportSuccess()),
              catchError((err) => {
                console.error('Error initializing create report', err);
                return of(
                  reportEvents.createReportFailed({
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                );
              })
            );
          })
        ),
        openReport$: events.on(reportEvents.openReport).pipe(
          exhaustMap(({ payload: { vesselId, reportId } }) =>
            forkJoin({
              formFieldRules: reportService.getFormFieldRules(),
              formRuleTemplate: getFormRuleTemplate(vesselId),
              vesselSpec: vesselSpecService.getVesselSpec(vesselId).pipe(catchError(() => of(undefined))),
              savedReport: reportService.getReport(vesselId, reportId),
              carryoverData: reportService.getCarryoverReport(vesselId, reportId),
            }).pipe(
              map(({ formFieldRules, formRuleTemplate, vesselSpec, savedReport, carryoverData }) => ({
                formFieldRules,
                formRuleTemplate,
                vesselSpec,
                savedReport: {
                  report: reportAdapterService.convertFromAPISavedReport(savedReport, vesselSpec),
                  // `comments` is a root-level report field, not part of IReport.
                  comments: (savedReport as { comments?: string })?.comments ?? '',
                },
                carryoverData: reportAdapterService.convertFromAPI(carryoverData, vesselSpec),
              })),
              tap(({ formFieldRules, formRuleTemplate, savedReport, carryoverData, vesselSpec }) => {
                const { report, comments } = savedReport;
                dispatcher.dispatch(reportEvents.setReportType(report.type));
                dispatcher.dispatch(reportEvents.setNotes(comments));
                dispatcher.dispatch(reportEvents.gotCarryoverData(carryoverData));
                dispatcher.dispatch(reportEvents.gotFormFieldRules(formFieldRules));
                dispatcher.dispatch(reportEvents.gotFormRuleTemplate(formRuleTemplate));
                initialiseSharedForms(report, INIT_MODES.IMPORT, formFieldRules, vesselSpec);
              }),
              map(({ savedReport }) =>
                reportEvents.openReportSuccess({ status: savedReport.report.status ?? ReportStatuses.Started })
              ),
              catchError((err) => {
                console.error('Error initializing open report', err);
                return of(reportEvents.openReportFailed({ notFound: isReportNotFoundError(err) }));
              })
            )
          )
        ),
        saveReport$: events.on(reportEvents.saveReport).pipe(
          exhaustMap(({ payload: { vesselId, reportId, reportType, notes, status, closeAfterSave } }) => {
            const collated = collateReport(reportType);
            const report = reportAdapterService.convertToAPI(collated);
            // Report comments are stored on the report store (not part of the
            // form sections) and go at the root of the payload as `comments`.
            report['comments'] = notes;

            return reportService.saveReport(vesselId, report, reportId).pipe(
              map((response) => {
                const savedReportId = (response as { report_id?: string })?.report_id ?? reportId ?? '';
                const reduced = buildReducedVesselReport(collated, reportType, {
                  reportId: savedReportId,
                  status,
                });
                return reportEvents.saveReportSuccess({ reportId: savedReportId, report: reduced, closeAfterSave });
              }),
              catchError((err) => {
                console.error('Error saving report', err);
                return of(
                  reportEvents.saveReportFailed({
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                );
              })
            );
          })
        ),
        saveReportSuccess$: events.on(reportEvents.saveReportSuccess).pipe(
          tap(({ payload: { reportId, report, closeAfterSave } }) => {
            toastDispatcher.toastSuccess({
              summary: 'Report Saved',
              detail: 'Your report has been saved successfully.',
            });

            // Hand the saved row to the reports list so it reflects the latest
            // data without a refetch. Notifying via the list's own event keeps
            // the dependency one-way (vessel-report -> vessel-reports).
            if (reportId) {
              dispatcher.dispatch(vesselReportsEvents.reportSaved({ report }));
            }

            if (closeAfterSave) {
              const vesselId = appStore.selectedVesselId();
              if (!vesselId) return;

              dispatcher.dispatch(reportEvents.reset());
              router.navigate(['site', vesselId, 'vessel-reports']);
              return;
            }

            if (!reportId) return;
            const [path, query] = router.url.split('?');
            const segments = path.split('/');
            const idIndex = segments.indexOf('vessel-reports') + 1;
            if (idIndex === 0 || !segments[idIndex] || segments[idIndex] === reportId) {
              return;
            }
            segments[idIndex] = reportId;
            const target = query ? `${segments.join('/')}?${query}` : segments.join('/');
            // Point the route at the server id (replacing history so the dead
            // local-id url isn't left behind). The report isn't reloaded: the
            // vessel-report route effect skips openReport for savedReportId,
            // which the reducer set from this same event.
            router.navigateByUrl(target, { replaceUrl: true });
          })
        ),
        saveReportFailed$: events.on(reportEvents.saveReportFailed).pipe(
          tap(({ payload: { error } }) => {
            toastDispatcher.toastError({
              summary: 'Save Failed',
              detail: `Your report could not be saved: ${error}`,
            });
          })
        ),
        submitReport$: events.on(reportEvents.submitReport).pipe(
          exhaustMap(({ payload: { vesselId, reportId, reportType, notes, submitType } }) => {
            const collated = collateReport(reportType);
            const report = reportAdapterService.convertToAPI(collated);
            // Report comments are stored on the report store (not part of the
            // form sections) and go at the root of the payload as `comments`.
            report['comments'] = notes;

            return reportService.saveReport(vesselId, report, reportId, submitType).pipe(
              map((response) => {
                // The submit/finalise response returns this report's id under
                // `id`. Its number field is the NEXT report's number, so the
                // reduced row takes the number from the form instead (see builder).
                const { report_id, id } = (response ?? {}) as {
                  report_id?: string;
                  id?: string;
                };
                const reduced = buildReducedVesselReport(collated, reportType, {
                  reportId: report_id ?? id ?? reportId ?? '',
                  status: ReportStatuses.Completed,
                });
                return reportEvents.submitReportSuccess({ report: reduced });
              }),
              catchError((err) => {
                console.error('Error submitting report', err);
                return of(
                  reportEvents.submitReportFailed({
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                );
              })
            );
          })
        ),
        submitReportSuccess$: events.on(reportEvents.submitReportSuccess).pipe(
          tap(({ payload: { report } }) => {
            toastDispatcher.toastSuccess({
              summary: 'Report Submitted',
              detail: 'Your report has been submitted successfully.',
            });

            // Hand the completed row to the reports list so it reflects the new
            // status without a refetch. Notifying via the list's own event keeps
            // the dependency one-way (vessel-report -> vessel-reports).
            dispatcher.dispatch(vesselReportsEvents.reportSubmitted({ report }));

            // Clear the finished report from the store so the side menu drops the
            // "Edit Report in Progress" item and returns to the create options.
            dispatcher.dispatch(reportEvents.reset());

            const vesselId = appStore.selectedVesselId();
            if (!vesselId) return;
            router.navigate(['site', vesselId, 'vessel-reports']);
          })
        ),
        submitReportFailed$: events.on(reportEvents.submitReportFailed).pipe(
          tap(({ payload: { error } }) => {
            toastDispatcher.toastError({
              summary: 'Submit Failed',
              detail: `Your report could not be submitted: ${error}`,
            });
          })
        ),
        deleteReport$: events.on(reportEvents.deleteReport).pipe(
          exhaustMap(({ payload: { vesselId, reportId } }) => {
            // A new (never-saved) report has no server id, so there is nothing
            // to delete remotely — skip the request and clean up locally.
            if (!reportId) {
              return of(reportEvents.deleteReportSuccess({ reportId: undefined }));
            }

            return reportService.deleteReport(vesselId, reportId).pipe(
              map(() => reportEvents.deleteReportSuccess({ reportId })),
              catchError((err) => {
                console.error('Error deleting report', err);
                return of(
                  reportEvents.deleteReportFailed({
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                );
              })
            );
          })
        ),
        deleteReportSuccess$: events.on(reportEvents.deleteReportSuccess).pipe(
          tap(({ payload: { reportId } }) => {
            toastDispatcher.toastSuccess({
              summary: 'Report Deleted',
              detail: 'Your report has been deleted successfully.',
            });

            // Drop the row from the reports list without a refetch. Notifying
            // via the list's own event keeps the dependency one-way
            // (vessel-report -> vessel-reports).
            dispatcher.dispatch(vesselReportsEvents.reportDeleted({ reportId }));

            // Clear the deleted report from the store, then return to the list.
            dispatcher.dispatch(reportEvents.reset());

            const vesselId = appStore.selectedVesselId();
            if (!vesselId) return;
            router.navigate(['site', vesselId, 'vessel-reports']);
          })
        ),
        deleteReportFailed$: events.on(reportEvents.deleteReportFailed).pipe(
          tap(({ payload: { error } }) => {
            toastDispatcher.toastError({
              summary: 'Delete Failed',
              detail: `Your report could not be deleted: ${error}`,
            });
          })
        ),
        // Total Electrical Work auto-tracks kwh + the carried-over running total (a
        // completed report subtracts kwh back out, since it's already folded into the
        // running total by then) — but only while the total still matches what
        // auto-calc would have produced from the *previous* kwh value. The moment a
        // user types a different total by hand, it stops re-syncing on future kwh
        // edits (power-vm-builder.ts's showTotalShorePowerModifiedWarning flags the
        // divergence instead).
        recalculateTotalShorePowerKwhOnKwhChange$: events.on(powerEvents.updateFormStateValue).pipe(
          filter(({ payload }) => payload.sectionId === 'shorePower' && payload.fieldId === 'kwh'),
          scan<{ payload: { value: unknown } }, { oldKwh: number | undefined; newKwh: number | undefined }>(
            (acc, { payload }) => ({ oldKwh: acc.newKwh, newKwh: payload.value as number | undefined }),
            { oldKwh: undefined, newKwh: undefined }
          ),
          map(({ oldKwh, newKwh }) => {
            const shorePower = powerStore.formState().shorePower;
            const currentTotal = shorePower?.['totalKwh']?.value as number | undefined;
            const runningTotal = shorePower?.['runningTotalKwh']?.value as number | undefined;
            const isCompleted = store.reportStatus() === ReportStatuses.Completed;

            const calculateTotal = (kwh: number | undefined) => {
              if (kwh == null || isNaN(kwh)) return undefined;
              if (runningTotal == null || isNaN(runningTotal)) return kwh;
              return kwh + runningTotal + (isCompleted ? -kwh : 0);
            };

            const wasAutoCalculated = currentTotal == null || currentTotal === calculateTotal(oldKwh);
            if (!wasAutoCalculated) return undefined;

            return powerEvents.updateFormStateValue({
              sectionId: 'shorePower',
              fieldId: 'totalKwh',
              value: calculateTotal(newKwh),
            });
          }),
          filter((event) => event !== undefined)
        ),
      };
    })
  );
}
